import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type InputHTMLAttributes,
  type KeyboardEvent,
  type Ref,
} from 'react';
import { Clock } from 'iconsax-reactjs';
import { formatPlainTime, isPlainTime, useTimeZone } from '@/lib/time';
import { useFieldProps } from '../Form/fieldContext';
import type { ControlSize } from '../Form/TextInput';
import { cx } from '../internal/cx';
import { Popover } from '../Popover/Popover';
import { buildTimeOptions, filterTimeOptions, minutesOf, parseTimeInput } from './timeOptions';
import formStyles from '../Form/Form.module.css';
import styles from './DateTime.module.css';

export interface TimePickerProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'value' | 'defaultValue' | 'onChange' | 'min' | 'max' | 'step' | 'type' | 'size'
> {
  /** Plain wall-clock time "HH:mm", or "" when empty. */
  value: string;
  onChange: (value: string) => void;
  /** Minutes between suggested times. Default 15. */
  step?: number;
  /** Earliest suggested/accepted time ("HH:mm"). */
  min?: string;
  /** Latest suggested/accepted time ("HH:mm"). */
  max?: string;
  size?: ControlSize;
  invalid?: boolean;
  ref?: Ref<HTMLInputElement>;
}

/** Time entry as an editable combobox with a listbox of suggested times. */
export function TimePicker({
  value,
  onChange,
  step = 15,
  min,
  max,
  size = 'md',
  invalid,
  id,
  required,
  disabled,
  readOnly,
  placeholder,
  className,
  onBlur,
  'aria-describedby': ariaDescribedBy,
  ref,
  ...rest
}: TimePickerProps) {
  const { locale } = useTimeZone();
  const listId = useId();
  const errorId = useId();
  const [draft, setDraft] = useState(() => formatPlainTime(value, locale));
  const [syncedValue, setSyncedValue] = useState(value);
  const [typed, setTyped] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [parseError, setParseError] = useState<string | null>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  if (value !== syncedValue) {
    setSyncedValue(value);
    setDraft(formatPlainTime(value, locale));
    setTyped(false);
    setParseError(null);
  }

  const field = useFieldProps({
    id,
    'aria-describedby': cx(parseError ? errorId : undefined, ariaDescribedBy) || undefined,
    invalid: invalid || Boolean(parseError) || undefined,
    required,
    disabled,
  });

  const allOptions = useMemo(
    () => buildTimeOptions(step, locale, min, max),
    [step, locale, min, max],
  );
  const options = typed ? filterTimeOptions(allOptions, draft) : allOptions;
  const activeOption = activeIndex >= 0 ? options[activeIndex] : undefined;
  const optionId = (index: number) => `${listId}-opt-${index}`;

  useEffect(() => {
    if (!open || activeIndex < 0) return;
    const el = listRef.current?.querySelector<HTMLElement>(`[data-index="${activeIndex}"]`);
    el?.scrollIntoView?.({ block: 'nearest' });
  }, [open, activeIndex]);

  function indexNearest(list: typeof options): number {
    if (list.length === 0) return -1;
    const exact = list.findIndex((o) => o.value === value);
    if (exact >= 0) return exact;
    if (!value || !isPlainTime(value)) return 0;
    const target = minutesOf(value);
    const after = list.findIndex((o) => minutesOf(o.value) >= target);
    return after >= 0 ? after : list.length - 1;
  }

  function openList() {
    if (disabled || readOnly) return;
    setOpen(true);
    setActiveIndex(indexNearest(options));
  }

  function outOfRange(time: string): boolean {
    const m = minutesOf(time);
    return Boolean(
      (min && isPlainTime(min) && m < minutesOf(min)) ||
      (max && isPlainTime(max) && m > minutesOf(max)),
    );
  }

  function commitValue(next: string) {
    setOpen(false);
    setTyped(false);
    setParseError(null);
    setDraft(formatPlainTime(next, locale));
    if (next !== value) onChange(next);
  }

  function commitText(text: string) {
    if (value && text === formatPlainTime(value, locale)) {
      setTyped(false);
      setParseError(null);
      return;
    }
    const trimmed = text.trim();
    if (!trimmed) {
      setTyped(false);
      setParseError(null);
      if (value !== '') onChange('');
      return;
    }
    const parsed = parseTimeInput(trimmed);
    if (!parsed) {
      setParseError('Enter a valid time, for example 8:30 AM.');
      return;
    }
    if (outOfRange(parsed)) {
      setParseError(
        `Enter a time between ${formatPlainTime(min ?? '00:00', locale)} and ${formatPlainTime(max ?? '23:59', locale)}.`,
      );
      return;
    }
    commitValue(parsed);
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (!open) openList();
        else setActiveIndex((i) => Math.min(options.length - 1, i + 1));
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (!open) openList();
        else setActiveIndex((i) => Math.max(0, i - 1));
        break;
      case 'PageDown':
        if (open) {
          event.preventDefault();
          setActiveIndex((i) => Math.min(options.length - 1, i + 4));
        }
        break;
      case 'PageUp':
        if (open) {
          event.preventDefault();
          setActiveIndex((i) => Math.max(0, i - 4));
        }
        break;
      case 'Enter':
        if (open && activeOption) {
          event.preventDefault();
          commitValue(activeOption.value);
        } else {
          commitText(event.currentTarget.value);
        }
        break;
      case 'Escape':
        if (open) {
          event.preventDefault();
          setOpen(false);
        }
        break;
      default:
        break;
    }
  }

  return (
    <div className={styles.pickerRoot}>
      <div
        ref={anchorRef}
        className={cx(
          formStyles.control,
          formStyles[`control-${size}`],
          field.invalid && formStyles.controlInvalid,
          field.disabled && formStyles.controlDisabled,
          readOnly && formStyles.controlReadOnly,
        )}
      >
        <span className={formStyles.adornment} aria-hidden="true">
          <Clock size={16} color="currentColor" />
        </span>
        <input
          ref={ref}
          type="text"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls={listId}
          aria-activedescendant={open && activeOption ? optionId(activeIndex) : undefined}
          autoComplete="off"
          id={field.id}
          className={cx(formStyles.input, styles.timeInput, className)}
          value={draft}
          placeholder={placeholder ?? formatPlainTime('08:00', locale)}
          aria-describedby={field.describedBy}
          aria-invalid={field.invalid || undefined}
          required={field.required || undefined}
          disabled={field.disabled || undefined}
          readOnly={readOnly}
          onChange={(event) => {
            const text = event.target.value;
            setDraft(text);
            setTyped(true);
            if (!open && !readOnly) setOpen(true);
            setActiveIndex(text.trim() ? 0 : indexNearest(allOptions));
          }}
          onClick={() => (open ? undefined : openList())}
          onBlur={(event) => {
            onBlur?.(event);
            setOpen(false);
            commitText(event.target.value);
          }}
          onKeyDown={onKeyDown}
          {...rest}
        />
      </div>
      {parseError && (
        <p id={errorId} className={formStyles.error}>
          {parseError}
        </p>
      )}
      <Popover
        open={open}
        onClose={() => setOpen(false)}
        anchorRef={anchorRef}
        panelRef={listRef}
        id={listId}
        role="listbox"
        aria-label="Suggested times"
        focusOnOpen={false}
        restoreFocus={false}
        matchWidth
        className={styles.timeList}
      >
        {options.map((option, index) => (
          // Keyboard interaction happens on the combobox input (aria-activedescendant).
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events
          <div
            key={option.value}
            id={optionId(index)}
            data-index={index}
            role="option"
            tabIndex={-1}
            aria-selected={option.value === value}
            className={cx(
              styles.timeOption,
              index === activeIndex && styles.timeOptionActive,
              option.value === value && styles.timeOptionSelected,
            )}
            // Keep focus in the input so typing continues to work.
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => commitValue(option.value)}
          >
            {option.label}
          </div>
        ))}
      </Popover>
    </div>
  );
}
